import { useTranslation } from "react-i18next";
import { XIcon } from "lucide-react";
import { DialogTitle } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import type { ProfileGroup, ProfileSection } from "./profile-dialog";

export interface DesktopProfileLayoutProps {
  groups: ProfileGroup[];
  activeSection: ProfileSection;
  activeSectionLabel: string;
  onSectionChange: (section: ProfileSection) => void;
  onClose: () => void;
  onSignOut: () => void;
  children: React.ReactNode;
}

export function DesktopProfileLayout({
  groups,
  activeSection,
  activeSectionLabel,
  onSectionChange,
  onClose,
  onSignOut,
  children,
}: DesktopProfileLayoutProps) {
  const { t } = useTranslation();
  const sectionGroups = groups.filter(
    (g): g is Extract<ProfileGroup, { kind: "sections" }> => g.kind === "sections",
  );
  return (
    <div className="hidden h-full overflow-hidden md:flex">
      <nav className="flex min-h-0 w-52 shrink-0 flex-col border-r border-line bg-surface-1 p-4">
        <DialogTitle className="mb-4 text-[13px] font-semibold text-ink-900">
          {t("profile-dialog.title")}
        </DialogTitle>
        <div className="-mx-1 min-h-0 flex-1 space-y-4 overflow-y-auto px-1">
          {sectionGroups.map((group) => (
            <div key={group.label}>
              <p className="mb-1 font-mono text-[9.5px] uppercase tracking-[.12em] text-ink-500">
                {group.label}
              </p>
              {group.items.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSectionChange(item.id)}
                  className={cn(
                    "flex w-full items-center gap-2 rounded-3 px-2 py-1.5 text-left text-[13px] transition-colors",
                    activeSection === item.id
                      ? "bg-surface font-medium text-ink-900 ring-1 ring-ink-900/10"
                      : "text-ink-700 hover:bg-surface/60 hover:text-ink-900",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ))}
        </div>
        <div className="shrink-0 border-t border-line pt-3">
          <button
            type="button"
            onClick={onSignOut}
            className="w-full rounded-3 px-2 py-1.5 text-left text-[13px] text-vroom-red hover:bg-vroom-red-soft"
          >
            {t("common.nav.sign-out.label")}
          </button>
        </div>
      </nav>
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-3.5">
          <h2 className="text-[15px] font-semibold text-ink-900">{activeSectionLabel}</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.buttons.close.label")}
            className="rounded-3 p-1 text-ink-500 hover:bg-surface-1 hover:text-ink-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-p-600"
          >
            <XIcon size={16} />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-6">{children}</div>
      </div>
    </div>
  );
}
